import React from 'react';
import { Check } from 'lucide-react';
import LazyImage from './LazyImage';
import { getAvatarUrl } from '../utils/imageUtils';
import { useOnlineStatus } from '../contexts/OnlineStatusContext';

// Normalize role strings into a comparable form
const norm = (v) => (v || '').toString().trim().toLowerCase();

// Decide if user is verified (badge visible)
export const isUserVerified = (user) => {
  if (!user) return false;
  if (user.is_super_admin === true) return true;
  const r = norm(user.role);
  // Canonical roles (Variante B + role normalization migration):
  // verified/full/admin/super_admin are "verified" for badge purposes.
  // Keep legacy aliases as defensive fallback.
  return (
    r === 'verified' ||
    r === 'full' ||
    r === 'admin' ||
    r === 'super_admin' ||
    // legacy
    r === 'провереный' ||
    r === 'проверенный' ||
    r === 'полный' ||
    r === 'админ' ||
    r === 'супер админ' ||
    r === 'суперадмин'
  );
};

/**
 * VerifiedAvatar
 * Props:
 *  - user: { role?, is_super_admin? }
 *  - src, alt
 *  - size: number (px), default 32
 *  - sizeClass: optional Tailwind size classes (e.g. "w-8 h-8 sm:w-10 sm:h-10") for responsive sizes
 *  - rounded: 'full' | 'lg' | 'md' (default 'full')
 *  - className: extra classes for wrapper
 *  - fallbackText: optional initial letter fallback (string)
 *  - badgePx: optional number to explicitly set badge size in px (overrides auto)
 *  - fontPx: optional number to explicitly set fallback initial font size in px
 *  - badgePositionClass: override badge position classes (default bottom-0 right-0)
 */
const VerifiedAvatar = ({
  user,
  userId,
  src,
  alt,
  size = 32,
  sizeClass = '',
  rounded = 'full',
  className = '',
  fallbackText = '',
  badgePx,
  fontPx,
  badgePositionClass = 'bottom-0 right-0',
  showOnline = true,
  onlinePositionClass = 'bottom-0 left-0',
  // Optional: allow caller to override online state (e.g. for Socket.IO presence lists)
  onlineOverride
}) => {
  const verified = isUserVerified(user);
  const { isUserOnline } = useOnlineStatus();

  const resolvedUserId = (userId != null) ? userId : (user?.id || user?.user_id || user?.userId);
  const online = (typeof onlineOverride === 'boolean')
    ? onlineOverride
    : !!(showOnline && resolvedUserId && isUserOnline(resolvedUserId));
  const roundedClass =
    rounded === 'lg' ? 'rounded-lg' : rounded === 'md' ? 'rounded-md' : 'rounded-full';

  const wrapperStyle = sizeClass ? undefined : { width: size, height: size };
  const wrapperClass = `relative inline-block ${sizeClass || ''} ${className}`.trim();
  const badgeSize = (badgePx != null) ? badgePx : (sizeClass ? 16 : Math.max(14, size * 0.35));
  const fontSize = (fontPx != null) ? fontPx : (sizeClass ? undefined : Math.max(12, size / 2.2));

  const resolvedSize = sizeClass ? null : Number(size || 0);
  const sizeFromClass = sizeClass ? (() => {
    // Try to parse Tailwind sizeClass like "w-10 h-10 sm:w-12 sm:h-12".
    // We only need a single base size in px to choose the right avatar variant.
    const match = String(sizeClass).match(/\bw-(\d+)\b/);
    if (!match) return null;
    const w = Number(match[1]);
    if (!Number.isFinite(w)) return null;
    // Tailwind spacing scale: w-10 => 2.5rem (40px), w-12 => 3rem (48px)
    return w * 4;
  })() : null;

  const buildVariantUrl = (avatarUrl, targetSize) => {
    const u = String(avatarUrl || '').trim();
    if (!u) return null;
    // If already an absolute URL, keep it as-is (can't safely derive variant).
    if (/^https?:\/\//i.test(u)) return u;
    // Our new naming convention: ...-512.webp, ...-80.webp, ...-44.webp
    if (/-512\.webp(\?.*)?$/i.test(u)) {
      return u.replace(/-512\.webp(\?.*)?$/i, `-${targetSize}.webp`);
    }
    // Legacy fallback: add suffix if it's a webp
    if (/\.webp(\?.*)?$/i.test(u)) {
      return u.replace(/\.webp(\?.*)?$/i, `-${targetSize}.webp`);
    }
    return u;
  };

  const getAvatarSrc = () => {
    if (!src) return null;
    const target = resolvedSize || sizeFromClass || null;
    if (target && target <= 44) return buildVariantUrl(src, 44);
    if (target && target <= 80) return buildVariantUrl(src, 80);
    return src;
  };

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      {src ? (
        <LazyImage
          src={getAvatarUrl(getAvatarSrc())}
          alt={alt}
          className={`w-full h-full object-cover ${roundedClass}`}
          placeholderClassName={`bg-gray-200 animate-pulse ${roundedClass}`}
          width={resolvedSize || sizeFromClass || undefined}
          height={resolvedSize || sizeFromClass || undefined}
        />
      ) : (
        <div className={`w-full h-full ${roundedClass} bg-primary-500 flex items-center justify-center`}>
          <span className="text-white font-bold" style={fontSize ? { fontSize } : undefined}>
            {fallbackText ? fallbackText[0]?.toUpperCase() : (alt ? alt[0]?.toUpperCase() : 'U')}
          </span>
        </div>
      )}

      {/* {verified && (
        <span
          className={`absolute ${badgePositionClass} translate-x-1 translate-y-1 inline-flex items-center justify-center rounded-full bg-blue-500 ring-2 ring-white`}
          style={{ width: badgeSize, height: badgeSize }}
          title="Проверенный аккаунт"
          aria-label="Проверенный аккаунт"
        >
          <Check className="text-white" style={{ width: '65%', height: '65%' }} />
        </span>
      )} */}

      {online && (
        <span
          className={`absolute ${onlinePositionClass} translate-x-1 translate-y-1 rounded-full bg-green-500 ring-2 ring-white`}
          style={{ width: Math.max(10, badgeSize * 0.5), height: Math.max(10, badgeSize * 0.5) }}
          title="Online"
          aria-label="Online"
        />
      )}
    </div>
  );
};

export default VerifiedAvatar;
